export type AccumulationMode = 'line' | 'chips' | 'both' | 'hidden'
export type WallHeadline = 'today' | '7-day' | '28-day'
export const wallHeadlineOrder: readonly WallHeadline[] = ['today', '7-day', '28-day']

export type DisplayPreferences = {
  dateFormat: 'day-month-year' | 'month-day-year' | 'iso'
  density: 'comfortable' | 'compact'
  scoreboardAccumulation: AccumulationMode
  wallAccumulation: AccumulationMode
  wallHeadlines: readonly WallHeadline[]
  /** Compatibility hero while the deployed Wall function rolls to wallHeadlines. */
  wallHeadline: WallHeadline
  scoreboardCategories: boolean
  scoreboardSuccessfulItems: boolean
  wallCategories: boolean
  wallSuccessfulItems: boolean
}

const key = 'ops-matrix-codex-display'
export const defaultDisplayPreferences: DisplayPreferences = {
  dateFormat: 'day-month-year',
  density: 'comfortable',
  scoreboardAccumulation: 'chips',
  wallAccumulation: 'line',
  wallHeadlines: ['today'],
  wallHeadline: 'today',
  scoreboardCategories: true,
  scoreboardSuccessfulItems: false,
  wallCategories: true,
  wallSuccessfulItems: false,
}

export function readDisplayPreferences(): DisplayPreferences {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '{}') as Partial<DisplayPreferences>
    const legacy = parsed as Partial<DisplayPreferences> & { wallHeadline?: WallHeadline }
    const headlines = Array.isArray(legacy.wallHeadlines) && legacy.wallHeadlines.length
      ? wallHeadlineOrder.filter((item) => legacy.wallHeadlines!.includes(item))
      : legacy.wallHeadline ? [legacy.wallHeadline] : defaultDisplayPreferences.wallHeadlines
    return { ...defaultDisplayPreferences, ...parsed, wallHeadlines: headlines, wallHeadline: headlines[0] ?? 'today' }
  } catch {
    return defaultDisplayPreferences
  }
}

export function subscribeDisplayPreferences(listener: (preferences: DisplayPreferences) => void): () => void {
  const changed = (event: Event) => listener((event as CustomEvent<DisplayPreferences>).detail ?? readDisplayPreferences())
  window.addEventListener('ops-matrix-display-change', changed)
  return () => window.removeEventListener('ops-matrix-display-change', changed)
}

export function formatPoints(value: number | null | undefined): string {
  return value == null ? '—' : Math.round(value).toLocaleString()
}

export function writeDisplayPreferences(preferences: DisplayPreferences): void {
  const selectedHeadlines = preferences.wallHeadline !== preferences.wallHeadlines[0]
    ? [preferences.wallHeadline]
    : preferences.wallHeadlines
  const normalized = { ...preferences, wallHeadlines: selectedHeadlines, wallHeadline: selectedHeadlines[0] ?? 'today' }
  localStorage.setItem(key, JSON.stringify(normalized))
  window.dispatchEvent(new CustomEvent('ops-matrix-display-change', { detail: normalized }))
}
